import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { DetailsQueries } from '../../queries/details.queries';
import { BookValuesQuery } from '../../interfaces/book-values.query';
import { Observable } from 'rxjs';
import { BookValueModalComponent } from '../../../../../vehicles/components/book-value-modal/book-value-modal.component';
import { InventoryItem, LaunchDarklyService, BookProviderService, DetailsState, UserProfileState } from 'remarketing-angular-library';
import { InventoryItemUtilService } from '../../../../../../src/sell/lib/sell-shared/services/inventory-item-util.service';
import { TranslocoService, TranslocoDirective } from '@jsverse/transloco';
import { SaComponent, CurrencyFormatService } from 'smartauction-frontend-micro-library';
import { take, takeUntil } from 'rxjs/operators';
import { BookValueTabsComponent } from 'remarketing-ui-components-lib';
import { AsyncPipe } from '@angular/common';

@Component({
    selector: 'app-book-value',
    templateUrl: './book-value.component.html',
    styleUrls: ['./book-value.component.scss'],
    imports: [TranslocoDirective, BookValueTabsComponent, BookValueModalComponent, AsyncPipe]
})
export class BookValueComponent extends SaComponent implements OnInit {
  @Select(DetailsQueries.bookValues) bookValues$: Observable<BookValuesQuery>;
  @Input() showHeading = true;
  @ViewChild(BookValueModalComponent) bookValueModal;

  bookProviderName: string;
  defaultBookValue = 0;
  inventoryItem: InventoryItem;
  bookValueTrans: any;

  formattedBlackBookValues = {
    wholesaleAverage: '',
    wholesaleClean: '',
    wholesaleRough: '',
    financeAdvance: '',
    averageTradeIn: ''
  };

  formattedNadaValues = {
    tradeInClean: '',
    tradeInAverage: '',
    tradeInRough: '',
    retailClean: '',
    loanClean: ''
  };

  formattedManheimValues = {
    above: '',
    average: '',
    below: ''
  };

  constructor(
    _bookProviderService: BookProviderService,
    private inventoryItemUtilService: InventoryItemUtilService,
    private readonly store: Store,
    private readonly translocoService: TranslocoService,
    private readonly _launchDarklyService: LaunchDarklyService,
    private readonly currencyFormatService: CurrencyFormatService
  ) {
    super();
    this.bookProviderName = _bookProviderService.bookProviderName;
  }

  get vehicleTitle() {
    return `${this.inventoryItem.itemDetail.modelYear || ''} ${this.inventoryItem.itemDetail.make || ''} ${this
      .inventoryItem.itemDetail.model || ''} ${this.inventoryItem.itemDetail.trim || ''}`;
  }

  get isCanadianTenantEnabled(): boolean {
    return this._launchDarklyService.getFeatureFlag('canadianTenantEnabled', false);
  }

  get isBookValuationManheimValuationEnabled(): boolean {
    return this._launchDarklyService.getFeatureFlag('bookValuation-manheimValuationEnabled', false);
  }

  openBookValueModal(event) {
    this.bookValueModal.show(event);
  }

  ngOnInit() {
    this.setDefaultBookValue();
    this.formatBookValues();
    this.inventoryItem = this.store.selectSnapshot(DetailsState.inventoryItem);
    this.translocoService.selectTranslateObject('UIComponents.bookValueTabs').subscribe(translatedObj => {
      this.bookValueTrans = translatedObj;
      this.formatBookValues();
    });
  }

  setDefaultBookValue() {
    this.store
      .select(UserProfileState.getDefaultBookProviderName)
      .pipe(take(1))
      .subscribe(bookProviderName => {
        if (bookProviderName && bookProviderName === 'NADA') {
          this.defaultBookValue = 1;
        } else if (bookProviderName && bookProviderName === 'Manheim Market Report') {
          this.defaultBookValue = 2;
        }
      });
  }

  onTabFocusChange(event) {
    this.inventoryItemUtilService.defaultBookValue = event;
  }

  formatBookValues() {
    this.bookValues$.pipe(takeUntil(this.destroyed$)).subscribe(bookValues => {
      this.formatBlackBookValues(bookValues);
      this.formatNadaValues(bookValues);
      this.formatManheimValues(bookValues);
    });
  }

  private formatNadaValues(values: BookValuesQuery) {
    if (values.nada) {
      if (values.nada['Trade-InClean']) {
        this.formattedNadaValues.tradeInClean = this.currencyFormatService.formatCurrency(values.nada['Trade-InClean'].valueAmount);
      }
      if (values.nada['Trade-InAverage']) {
        this.formattedNadaValues.tradeInAverage = this.currencyFormatService.formatCurrency(values.nada['Trade-InAverage'].valueAmount);
      }
      if (values.nada['Trade-InRough']) {
        this.formattedNadaValues.tradeInRough = this.currencyFormatService.formatCurrency(values.nada['Trade-InRough'].valueAmount);
      }
      if (values.nada.RetailClean) {
        this.formattedNadaValues.retailClean = this.currencyFormatService.formatCurrency(values.nada.RetailClean.valueAmount);
      }
      if (values.nada.LoanClean) {
        this.formattedNadaValues.loanClean = this.currencyFormatService.formatCurrency(values.nada.LoanClean.valueAmount);
      }
    }
  }

  private formatBlackBookValues(values: BookValuesQuery) {
    if (values.blackBook) {
      if (values.blackBook.WholesaleClean) {
        this.formattedBlackBookValues.wholesaleClean = this.currencyFormatService.formatCurrency(values.blackBook.WholesaleClean.valueAmount);
      }
      if (values.blackBook.WholesaleAverage) {
        this.formattedBlackBookValues.wholesaleAverage = this.currencyFormatService.formatCurrency(values.blackBook.WholesaleAverage.valueAmount);
      }
      if (values.blackBook.WholesaleRough) {
        this.formattedBlackBookValues.wholesaleRough = this.currencyFormatService.formatCurrency(values.blackBook.WholesaleRough.valueAmount);
      }
      if (values.blackBook.FinanceAdvance) {
        this.formattedBlackBookValues.financeAdvance = this.currencyFormatService.formatCurrency(values.blackBook.FinanceAdvance.valueAmount);
      }
      if (values.blackBook['AverageTrade-In']) {
        this.formattedBlackBookValues.averageTradeIn = this.currencyFormatService.formatCurrency(values.blackBook['AverageTrade-In'].valueAmount);
      }
    }
  }

  private formatManheimValues(values: BookValuesQuery) {
    if (values.manheim) {
      if (values.manheim['Above']) {
        this.formattedManheimValues.above = this.currencyFormatService.formatCurrency(values.manheim['Above'].valueAmount);
      }
      if (values.manheim['Average']) {
        this.formattedManheimValues.average = this.currencyFormatService.formatCurrency(values.manheim['Average'].valueAmount);
      }
      if (values.manheim['Below']) {
        this.formattedManheimValues.below = this.currencyFormatService.formatCurrency(values.manheim['Below'].valueAmount);
      }
    }
  }
}
